<script setup>
/**
 * Painel de raciocínio do agente de IA da SuperTVGo.
 *
 * O backend (serviço supertvgo) cria uma nota privada assim que o agente
 * começa a pensar e REESCREVE o texto dela a cada ferramenta que termina. Como
 * o Chatwoot empurra toda atualização de mensagem pela websocket, este
 * componente se redesenha sozinho: o atendente vê os passos aparecendo em
 * tempo real, com o ícone girando enquanto ainda há trabalho em andamento.
 *
 * O estado vem num marcador HTML na primeira linha do conteúdo, não em
 * `content_attributes`: a API do Chatwoot aceita `content_attributes` na
 * criação da mensagem mas ignora no PATCH, então o texto é o único canal que
 * sobrevive às atualizações.
 *
 * Formato que o backend envia:
 *
 *   <!--supertvgo_raciocinio:pensando-->
 *   🧠 Raciocínio do agente
 *
 *   ✅ Conferindo se já testou antes → nunca testou
 *   ⏳ Liberando teste grátis
 *
 * O rótulo de cada passo vem pronto do backend, em português: são nomes de
 * ação do agente, não interface. O que é interface (título, contador) passa
 * pelo i18n como o resto do painel.
 */
import { computed, ref, watch } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import { useMessageContext } from '../provider.js';

const { content } = useMessageContext();

const MARCADOR = /^<!--supertvgo_raciocinio:(pensando|pronto)-->/;

/** true enquanto o agente ainda está trabalhando nesta rodada. */
const pensando = computed(() => {
  const achado = MARCADOR.exec(content.value ?? '');
  return achado?.[1] === 'pensando';
});

/**
 * As linhas de passo, já sem o marcador e sem o cabeçalho. Cada passo carrega
 * o próprio ícone no texto (⏳ ✅ ⚠️), que é o que separa o que terminou do que
 * ainda está rodando.
 */
const linhas = computed(() =>
  (content.value ?? '')
    .replace(MARCADOR, '')
    .split('\n')
    .map(linha => linha.trim())
    .filter(linha => linha && !linha.startsWith('🧠'))
);

const passos = computed(() =>
  linhas.value
    .filter(linha => !linha.startsWith('💬'))
    .map(linha => {
      const [, icone = '', texto = linha] =
        /^(⏳|✅|⚠️)\s*(.*)$/.exec(linha) ?? [];
      return { icone, texto, emAndamento: icone === '⏳' };
    })
);

/**
 * A justificativa da resposta, que o agente escreve no fim de cada rodada.
 * Ganha bloco próprio: é o que o atendente lê para julgar se a resposta foi
 * boa, enquanto a lista acima só conta o que foi executado.
 */
const motivo = computed(() => {
  const linha = linhas.value.find(l => l.startsWith('💬'));
  return linha ? linha.replace(/^💬\s*/, '') : '';
});

const concluidos = computed(
  () => passos.value.filter(p => !p.emAndamento).length
);

// Aberto por padrão enquanto pensa: é justamente quando o atendente quer ver o
// que está acontecendo. Depois de pronto, respeita o que a pessoa escolheu.
const aberto = ref(pensando.value);
watch(pensando, agora => {
  if (agora) aberto.value = true;
});
</script>

<template>
  <BaseBubble class="px-3 py-2" data-bubble-name="agent-reasoning">
    <div class="flex flex-col gap-2 min-w-[15rem]">
      <button
        type="button"
        class="flex items-center gap-2 text-left bg-transparent border-0 cursor-pointer p-0 text-n-amber-12"
        @click="aberto = !aberto"
      >
        <span
          v-if="pensando"
          class="i-lucide-loader-circle animate-spin size-4 flex-shrink-0"
        />
        <span v-else class="i-lucide-brain size-4 flex-shrink-0" />

        <span class="text-sm font-medium">
          {{
            pensando
              ? $t('CONVERSATION.AGENT_REASONING.THINKING')
              : $t('CONVERSATION.AGENT_REASONING.TITLE')
          }}
        </span>

        <span class="text-xs opacity-70">
          {{ $t('CONVERSATION.AGENT_REASONING.STEPS', concluidos) }}
        </span>

        <span
          class="size-4 flex-shrink-0 ml-auto"
          :class="aberto ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        />
      </button>

      <ul v-if="aberto" class="flex flex-col gap-1 m-0 pl-0 list-none">
        <li
          v-for="(passo, indice) in passos"
          :key="indice"
          class="flex items-start gap-2 text-sm"
        >
          <span
            v-if="passo.emAndamento"
            class="i-lucide-loader-circle animate-spin size-3.5 mt-0.5 flex-shrink-0"
          />
          <span v-else class="flex-shrink-0">{{ passo.icone }}</span>
          <span>{{ passo.texto }}</span>
        </li>
      </ul>

      <p
        v-if="aberto && motivo"
        class="p-2 m-0 text-sm rounded-lg bg-n-alpha-2"
      >
        {{ motivo }}
      </p>
    </div>
  </BaseBubble>
</template>
